import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { forwardRef, useCallback, useContext, useEffect, useRef, useState } from 'react';
import CustomSelect2 from './CustomSelect2';
import { Box, Typography, FormControlLabel, Checkbox, Grid, TextField, InputAdornment, Select, MenuItem, colors, Button, Skeleton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Chip, Snackbar } from '@mui/material';
import SelectPlanCheckbox from './SelectPlanCheckbox';
import { ConfigContext } from '../utils/ConfigContext';
import TableRatesRow from './TableRatesRow';
import getSafe from '../utils/getSafe';
import domtoimage from 'dom-to-image';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CloseIcon from '@mui/icons-material/Close';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import PercentInput from './PercentInput';
import NotesDisplay from './NotesDisplay';
import RatesVersionSelect from './RatesVersionSelect';
import TableViewModeToggleButton from './TableViewModeToggleButton';
import CopyImageButton from './CopyImageButton';
import TableCellText from './TableCellText';
import _ from 'lodash';
import TableCellRate from './TableCellRate';
import TableCellRateSubtotalPerPlan from './TableCellRateSubtotalPerPlan';
import CopyExcelDataButton from './CopyExcelDataButton';

const BWHO_Rates = forwardRef(({ appConfig, sx, clients, loadingStatus, currency, selectedPlans, setSelectedPlans,
  response, deductibles, setDeductibles, addToPendingTasks,
  discounts, setDiscounts, computedRates, setComputedRates,
  specificAreaOfCoverage, setSpecificAreaOfCoverage, areaOfCoverage,
  setShowImageCopy, selectedOptions, addSelectedOption,
  setViewSelectedOption, specificCurrency, setSpecificCurrency,
  specificPaymentFrequency, setSpecificPaymentFrequency, loadings, setLoadings,
  isExpandedApiRequestPerInsurers, setIsxpandedApiRequestPerInsurers , insurerRates = [], selectedRatesVersion, setSelectedRatesVersion, handleFetchAppConfig, setShowRefreshNow }, ref) => {
  // const { appConfig } = useContext(ConfigContext);

  const id = 'bwho';

  const [assistanceCovers, setAssistanceCovers] = useState({
    evacuation: false
  });

  const [comparisonDialogOpen, setComparisonDialogOpen] = useState(false);
  const [comparisonSelection, setComparisonSelection] = useState({
    wmi: false, wmp: false, wme: false, ww: false, evacuation: false
  });
  const [comparisons, setComparisons] = useState([]);
  const comparisonIdCounter = useRef(0);

  // While comparing, the plan checkboxes are driven by the comparisons and locked
  const isComparing = comparisons.length > 0;
  const [showComparingLockedSnackbar, setShowComparingLockedSnackbar] = useState(false);
  const onComparingLockedChange = isComparing ? () => setShowComparingLockedSnackbar(true) : null;
  const comparingCheckboxSx = isComparing ? { color: '#0c1b4e', '&.Mui-checked': { color: '#0c1b4e' } } : undefined;

  const containerRef = useRef(null);

  const WMI_Deductibles = getSafe(() => appConfig.content.deductibles.bwho.wmi);

  const WMP_Deductibles = getSafe(() => appConfig.content.deductibles.bwho.wmp);

  const WME_Deductibles = getSafe(() => appConfig.content.deductibles.bwho.wme);

  const WW_Deductibles = getSafe(() => appConfig.content.deductibles.bwho.ww);

  const bwho_Plans = ['bwho_wmi', 'bwho_wmp', 'bwho_wme', 'bwho_ww'];

  const specific_area_of_coverages = getSafe(() => appConfig.content.set2_area_of_coverage);

  const currencies = ["USD", "EURO", "GBP", "SGD", "AUD"];
  // const currencies = ["USD", "SGD"];

  /** CODE RELATED TO TABLE ROW HEIGHT */
  const tableRef = useRef(null);
  const [maxRowHeight, setMaxRowHeight] = useState(35);

  // Function to calculate max height
  const calculateMaxRowHeight = useCallback(() => {
    if (tableRef.current) {
      const tableRows = tableRef.current.querySelectorAll('tbody tr');
      const heights = Array.from(tableRows).map(row => row.offsetHeight);
      
      if (heights.length > 0) {
        const maxHeight = Math.max(...heights);
        setMaxRowHeight(maxHeight);
        return maxHeight;
      }
    }
    return 0;
  }, []);

  // Handle data changes
  useEffect(() => {
    // Use setTimeout to ensure DOM is fully rendered
    const timer = setTimeout(() => {
      calculateMaxRowHeight();
    }, 0);

    return () => clearTimeout(timer);
  }, [clients, response, calculateMaxRowHeight]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      calculateMaxRowHeight();
    };

    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [calculateMaxRowHeight]);
  /** END CODE RELATED TO TABLE ROW HEIGHT */

  useEffect(() => {
    computedRates[id] = getComputedRates();
    setComputedRates(computedRates);
  }, [response, discounts, loadings]);

  useEffect(() => {
    if (!selectedPlans.includes('bwho_wmi')) {
      setSelectedPlans([...selectedPlans, 'bwho_wmi']);
    }
  }, [selectedPlans]);

  // While comparing, the plan checkboxes and table rows reflect only the modules used by the comparisons
  useEffect(() => {
    if (comparisons.length === 0) { return; }

    const used = { wmi: false, wmp: false, wme: false, ww: false, evacuation: false };

    comparisons.forEach((comparison) => {
      Object.keys(used).forEach((plan) => {
        used[plan] = used[plan] || !!comparison[plan];
      });
    });

    applyPlanSelection(used);
  }, [comparisons]);

  // Checks exactly the given modules (wmi, wmp, wme, ww, evacuation) in the plan checkboxes
  function applyPlanSelection(plans) {
    const nextPlans = [
      ...selectedPlans.filter((plan) => !bwho_Plans.includes(plan)),
      ...bwho_Plans.filter((plan) => plans[plan.replace('bwho_', '')]),
    ];

    const isSamePlans = nextPlans.length === selectedPlans.length && nextPlans.every((plan) => selectedPlans.includes(plan));

    if (!isSamePlans) {
      setSelectedPlans(nextPlans);
    }

    if (assistanceCovers.evacuation !== !!plans.evacuation) {
      setAssistanceCovers({ ...assistanceCovers, evacuation: !!plans.evacuation });
    }
  }

  useEffect(() => {
    setTimeout(() => {
      addSelectedOption(id, 'specific_area_of_coverage', specificAreaOfCoverage[id]);
      addSelectedOption(id, 'specific_currency', specificCurrency[id]);
      addSelectedOption(id, 'specific_payment_frequency', specificPaymentFrequency[id]);
      addSelectedOption(id, 'wmi_deductible', deductibles.bwho.wmi_deductible);
      addSelectedOption(id, 'wmp_deductible', deductibles.bwho.wmp_deductible);
      addSelectedOption(id, 'wme_deductible', deductibles.bwho.wme_deductible);
      addSelectedOption(id, 'ww_deductible', deductibles.bwho.ww_deductible);
      addSelectedOption(id, 'discount', discounts[id] == '' ? 0 : discounts[id]);
      addSelectedOption(id, 'loading', loadings[id] == '' ? 0 : loadings[id]);
      addSelectedOption(id, 'evacuation', assistanceCovers.evacuation);
      addSelectedOption(id, 'comparisons', JSON.stringify(comparisons));
    }, 1000);
  }, [assistanceCovers.evacuation, deductibles.bwho.wmi_deductible, discounts[id], loadings[id], specificAreaOfCoverage[id], specificCurrency[id], specificPaymentFrequency[id], comparisons]);

  useEffect(() => {
    setViewSelectedOption(id, 'specific_area_of_coverage', (option_value) => {
      specificAreaOfCoverage[id] = option_value;
      setSpecificAreaOfCoverage(specificAreaOfCoverage);
    });

    setViewSelectedOption(id, 'specific_currency', (option_value) => {
      specificCurrency[id] = option_value;
      setSpecificCurrency(specificCurrency);
    });

    setViewSelectedOption(id, 'specific_payment_frequency', (option_value) => {
      specificPaymentFrequency[id] = option_value;
      setSpecificPaymentFrequency(specificPaymentFrequency);
    });

    setViewSelectedOption(id, 'wmi_deductible', (option_value) => {
      deductibles[id]['wmi_deductible'] = option_value;
      setDeductibles(deductibles);
    });

    setViewSelectedOption(id, 'wme_deductible', (option_value) => {
      deductibles[id]['wme_deductible'] = option_value;
      setDeductibles(deductibles);
    });

    setViewSelectedOption(id, 'wmp_deductible', (option_value) => {
      deductibles[id]['wmp_deductible'] = option_value;
      setDeductibles(deductibles);
    });

    setViewSelectedOption(id, 'ww_deductible', (option_value) => {
      deductibles[id]['ww_deductible'] = option_value;
      setDeductibles(deductibles);
    });

    setViewSelectedOption(id, 'discount', (option_value) => {
      discounts[id] = option_value;
      setDiscounts(discounts);
    });

    setViewSelectedOption(id, 'loading', (option_value) => {
      loadings[id] = option_value;
      setLoadings(loadings);
    });

    setViewSelectedOption(id, 'evacuation', (option_value) => {
      assistanceCovers.evacuation = option_value == 1;
      setAssistanceCovers(assistanceCovers);
    });

    setViewSelectedOption(id, 'comparisons', (option_value) => {
      let parsed = option_value;
      if (typeof parsed === 'string') {
        try {
          parsed = JSON.parse(parsed);
        } catch (e) {
          parsed = [];
        }
      }
      parsed = Array.isArray(parsed) && parsed.length > 1 ? parsed : [];

      setComparisons(parsed);

      const maxId = parsed.reduce((max, comparison) => Math.max(max, comparison.id || 0), 0);
      comparisonIdCounter.current = Math.max(comparisonIdCounter.current, maxId);
    });
  }, [selectedOptions]);

  function getComputedRates() {
    if (!response) return;

    const rates = response.rates;
    const assistance_cover_rates = response.assistance_covers;
    const discount = parseFloat(discounts[id]) || 0;
    const loading = parseFloat(loadings[id]) || 0;
    const totalPercent = ((100 - discount + loading) * 0.01);

    const computedRates = [];

    for (let i = 0; i < rates.length; i++) {
      const wmi_rate = rates[i].wmi;
      const wmp_rate = rates[i].wmi + rates[i].wmp;
      const wme_rate = rates[i].wmi + rates[i].wmp + rates[i].wme;
      const ww_rate = rates[i].wmi + rates[i].wmp + rates[i].wme + rates[i].ww;

      // const wmi_rate = rates[i].wmi;
      // const wmp_rate = rates[i].wmp;
      // const wme_rate = rates[i].wme;
      // const ww_rate = rates[i].ww;

      const computedRate = {
        'client': rates[i].client,
        'currency': rates[i].currency,
        'wmi': wmi_rate * totalPercent,
        'wmp': wmp_rate * totalPercent,
        'wme': wme_rate * totalPercent,
        'ww': ww_rate * totalPercent,
        'wmi_raw': rates[i].wmi * totalPercent,
        'wmp_raw': rates[i].wmp * totalPercent,
        'wme_raw': rates[i].wme * totalPercent,
        'ww_raw': rates[i].ww * totalPercent,
        'evacuation': assistance_cover_rates[i].evacuation * totalPercent,
      };

      const rates_per_deductibles = {};

      if (isExpandedApiRequestPerInsurers[id]) {
        for (const plan in rates[i]?.rates_per_deductibles) {

          rates_per_deductibles[plan] = {};

          for (const deductible in rates[i]?.rates_per_deductibles?.[plan]) {
            rates_per_deductibles[plan][deductible] = rates[i]?.rates_per_deductibles?.[plan]?.[deductible] * totalPercent;
          }
        }
      }

      computedRate['rates_per_deductibles'] = rates_per_deductibles;

      computedRates.push(computedRate);
    }

    return computedRates;
  }


  function checkIfThereIsSelected_BWHO_Plan() {
    for (let i = 0; i < selectedPlans.length; i++) {
      if (bwho_Plans.includes(selectedPlans[i])) {
        return true;
      }
    }

    return false;
  }

  function scrollToView() {
    // setTimeout(() => {
    //   containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // }, 200);
  }

  function onInputDiscountChange(e) {
    let value = e.target.value;

    // Allow only numbers and at most one decimal point
    value = value.replace(/[^0-9.]/g, '');

    // Prevent multiple decimals
    if ((value.match(/\./g) || []).length > 1) {
      value = value.substring(0, value.lastIndexOf('.'));
    }

    // Ensure only two decimal places
    if (value.includes('.')) {
      const [integerPart, decimalPart] = value.split('.');
      value = `${integerPart}.${decimalPart.substring(0, 2)}`;
    }

    // Remove leading zeros (but allow "0" or "0.x" scenarios)
    if (value !== '' && value !== '0' && value.startsWith('0') && !value.startsWith('0.')) {
      value = value.replace(/^0+/, '');
    }

    // Limit value to a maximum of 100
    if (parseFloat(value) > 100) {
      value = '100';
    }

    discounts[id] = value;

    setDiscounts({ ...discounts });
  }

  // function getPlanLabel(plan) {
  //   if (plan === 'wmi') {
  //     return 'WMI';
  //   } else if (plan === 'wmp') {
  //     return 'WMI + WMP';
  //   } else if (plan === 'wme') {
  //     return 'WMI + WMP + WME';
  //   } else if (plan === 'ww') {
  //     return 'WMI + WMP + WME + WW';
  //   }
  // }

  function getLivePlanSelection() {
    return {
      wmi: selectedPlans.includes('bwho_wmi'),
      wmp: selectedPlans.includes('bwho_wmp'),
      wme: selectedPlans.includes('bwho_wme'),
      ww: selectedPlans.includes('bwho_ww'),
      evacuation: !!assistanceCovers.evacuation,
    };
  }

  function buildPlanLabel(plans) {
    let plan = '';

    const append = (text) => {
      if (plan) {
        plan += ' + ';
      }
      plan += text;
    };

    if (plans.wmi) {
      append('WMI');
    }

    if (plans.wmp) {
      append('WMP');
    }

    if (plans.wme) {
      append('WME');
    }

    if (plans.ww) {
      append('WW');
    }

    if (plans.evacuation) {
      append('WE');
    }

    return plan;
  }

  function getPlanLabel() {
    return buildPlanLabel(getLivePlanSelection());
  }

  function openComparisonDialog() {
    setComparisonSelection({ wmi: true, wmp: false, wme: false, ww: false, evacuation: false });
    setComparisonDialogOpen(true);
  }

  function toggleComparisonSelection(field) {
    if (field === 'wmi') { return; }

    setComparisonSelection((prev) => ({ ...prev, [field]: !prev[field] }));
  }

  function nextComparisonId() {
    comparisonIdCounter.current += 1;
    return comparisonIdCounter.current;
  }

  function isSamePlanSelection(a, b) {
    return a.wmi === b.wmi && a.wmp === b.wmp && a.wme === b.wme && a.ww === b.ww && a.evacuation === b.evacuation;
  }

  function addComparison() {
    const existing = comparisons.length === 0 ? [getLivePlanSelection()] : comparisons;
    const isDuplicate = existing.some((comparison) => isSamePlanSelection(comparison, comparisonSelection));

    if (isDuplicate) {
      setComparisonDialogOpen(false);
      return;
    }

    setComparisons((prev) => {
      const next = [...prev];

      if (next.length === 0) {
        next.push({ id: nextComparisonId(), ...getLivePlanSelection() });
      }

      next.push({ id: nextComparisonId(), ...comparisonSelection });

      return next;
    });
    setComparisonDialogOpen(false);
  }

  function removeComparison(comparisonId) {
    const next = comparisons.filter((comparison) => comparison.id !== comparisonId);

    // A single comparison is no comparison, revert to the default dynamic total row
    // with the remaining combination checked
    if (next.length === 1) {
      applyPlanSelection(next[0]);
    }

    setComparisons(next.length <= 1 ? [] : next);
  }

  function getPlanSortRank(plans) {
    const planOrder = ['wmi', 'wmp', 'wme', 'ww', 'evacuation'];

    let rank = 0;

    planOrder.forEach((plan, index) => {
      if (plans[plan]) {
        rank += Math.pow(2, index);
      }
    });

    return rank;
  }

  function getSortedComparisons() {
    return [...comparisons].sort((a, b) => getPlanSortRank(a) - getPlanSortRank(b));
  }

  function formatRate(rate) {
    if (isNaN(rate) || rate == 'Unavailable' || typeof rate === 'string') {
      return <span style={{ color: '#f44336' }}>{'Unavailable'}</span>
    }

    if (isNaN(rate)) {
      return rate;
    }

    return getCurrencySign() + rate.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function getCurrencySign() {
    const currency = getCurrency();

    if (currency === 'USD') {
      return '$';
    } else if (currency === 'EURO') {
      return '€';
    } else if (currency === 'GBP') {
      return '£';
    } else if (currency === 'SGD') {
      return 'S$';
    } else if (currency === 'AUD') {
      return 'AU$';
    }

    return '';
  }

  function getCurrency() {
    const computedRates = getComputedRates();

    if (!computedRates) return '';

    if (computedRates.length === 0) { return '' }

    return computedRates[0].currency;
  }

  function computeTotalPremiumForPlansPerClient(plans, clientIndex) {
    const computedRates = getComputedRates();

    if (!computedRates) return 0;

    const rates = computedRates[clientIndex];

    if (!rates) return 0;

    let total = 0;

    const wmi = rates.wmi_raw;
    const wmp = rates.wmp_raw;
    const wme = rates.wme_raw;
    const ww = rates.ww_raw;

    if (plans.wmi) {
      if (!isNaN(wmi)) {
        total += wmi;
      }

      if (plans.wmp) {
        if (!isNaN(wmp)) {
          total += wmp;
        }
      }

      if (plans.wme) {
        if (!isNaN(wme)) {
          total += wme;
        }
      }

      if (plans.ww) {
        if (!isNaN(ww)) {
          total += ww;
        }
      }
    }

    if (plans.evacuation) {
      const evacuation = rates.evacuation;

      if (!isNaN(evacuation)) {
        total += evacuation;
      }
    }

    return total;
  }

  function computeTotalPremiumForPlansPerPlan(plans) {
    let total = 0;

    for (let i = 0; i < clients.length; i++) {
      const totalPerClient = computeTotalPremiumForPlansPerClient(plans, i);
      if (!isNaN(totalPerClient)) {
        total += totalPerClient;
      }
    }

    return total;
  }

  function computeTotalPremiumPerPlan() {
    return computeTotalPremiumForPlansPerPlan(getLivePlanSelection());
  }

  function computeTotalPremiumPerClient(clientIndex) {
    return computeTotalPremiumForPlansPerClient(getLivePlanSelection(), clientIndex);
  }

  const getComputedRatesMemo = _.memoize(getComputedRates);

  function getRate(clientIndex, plan) {
    const computedRates = getComputedRatesMemo();

    if (!computedRates) {
      return;
    }

    if (specificCurrency[id] !== computedRates?.[clientIndex]?.currency) {
      return '-';
    }

    return computedRates?.[clientIndex]?.[plan];
  }

  function getRateForExpandedTable(clientIndex, deductible, plan) {
    const computedRates = getComputedRatesMemo();

    if (!computedRates) {
      return;
    }

    if (specificCurrency[id] !== computedRates?.[clientIndex]?.currency) {
      return '-';
    }

    return computedRates?.[clientIndex]?.rates_per_deductibles?.[plan]?.[deductible];
  }

  function getRateSubTotalForExpandedTable(deductible, plan) {
    const computedRates = getComputedRatesMemo();

    if (!computedRates) {
      return;
    }

    if (specificCurrency[id] !== computedRates?.[0]?.currency) {
      return '-';
    }

    let subTotal = 0;

    for (let i = 0; i < clients.length; i++) {
      const rate = computedRates?.[i]?.rates_per_deductibles?.[plan]?.[deductible];

      if (isNaN(rate)) {
        continue;
      }

      subTotal += rate;
    }

    return subTotal;
  }

  function getCurrency() {
    const computedRates = getComputedRatesMemo();

    if (!computedRates) {
      return;
    }

    if (computedRates.length > 0) {
      return computedRates[0].currency;
    }

    return 'USD';
  }

  return (
    <Box id={id} sx={sx}>
      <Paper sx={{ borderColor: '#e0e0e0', borderWidth: 2, pb: 2, scrollMarginTop: '200px' }} variant='outlined' ref={containerRef}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#0c1b4e', px: 2, py: 1 }}>
          <div style={{
            color: '#fff',
            backgroundColor: '#0369ae',
            width: 100,
            height: 60,
            padding: 6,
            fontFamily: 'Poppins, sans-serif',
            fontSize: 13,
            lineHeight: 1.2,
          }}>Worldwide Health Options</div>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <img src={`${process.env.PUBLIC_URL}/images/Raffles Health Insurance.png`} alt="Raffles Health Insurance" style={{ height: 60, userSelect: 'none', WebkitUserDrag: 'none' }} draggable={false} onContextMenu={(e) => e.preventDefault()} />
            <img src={`${process.env.PUBLIC_URL}/images/BUPA Blue Logo.png`} alt="BUPA" style={{ height: 60, userSelect: 'none', WebkitUserDrag: 'none' }} draggable={false} onContextMenu={(e) => e.preventDefault()} />
          </Box>
        </Box>

        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 5, position: 'relative' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box sx={{ display: 'flex' }}>
                <Typography fontSize={15} fontWeight={600} width={260}>Plan Type</Typography>
                <Typography fontSize={15} fontWeight={600}>Deductible Options</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel sx={{ width: 260, mb: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }} control={<SelectPlanCheckbox size="small" readOnly sx={comparingCheckboxSx} onReadOnlyChange={onComparingLockedChange} onChangeCb={scrollToView} selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='bwho_wmi' />} label="Worldwide Medical Insurance" />
                <CustomSelect2
                  dense
                  sx={{ minWidth: 170 }}
                  value={deductibles.bwho.wmi_deductible}
                  onChange={(e) => {
                    const val = e.target.value;

                    const _deductibles = JSON.parse(JSON.stringify(deductibles));

                    _deductibles.bwho.wmi_deductible = val;

                    if (_deductibles.bwho.wmi_deductible === WMI_Deductibles[specificCurrency[id]][0]) {
                      _deductibles.bwho.wmp_deductible = WMP_Deductibles[specificCurrency[id]][0];
                      _deductibles.bwho.wme_deductible = WME_Deductibles[specificCurrency[id]][0];
                    } else {
                      _deductibles.bwho.wmp_deductible = WMP_Deductibles[specificCurrency[id]][1];
                      _deductibles.bwho.wme_deductible = WME_Deductibles[specificCurrency[id]][1];
                    }

                    setDeductibles(_deductibles);

                    addToPendingTasks([id]);

                  }}
                  items={WMI_Deductibles[specificCurrency[id]]}
                  disabled={loadingStatus[id]}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel sx={{ width: 260, mb: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }} control={<SelectPlanCheckbox size="small" readOnly={isComparing} sx={comparingCheckboxSx} onReadOnlyChange={onComparingLockedChange} onChangeCb={scrollToView} selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='bwho_wmp' />} label="Worldwide Medical Plus" />
                <CustomSelect2
                  dense
                  sx={{ minWidth: 170 }}
                  readOnly
                  value={deductibles.bwho.wmp_deductible}
                  items={WMP_Deductibles[specificCurrency[id]]}
                  disabled={loadingStatus[id]}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel sx={{ width: 260, mb: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }} control={<SelectPlanCheckbox size="small" readOnly={isComparing} sx={comparingCheckboxSx} onReadOnlyChange={onComparingLockedChange} onChangeCb={scrollToView} selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='bwho_wme' />} label="Worldwide Medicines & Equipment" />
                <CustomSelect2
                  dense
                  sx={{ minWidth: 170 }}
                  readOnly
                  value={deductibles.bwho.wme_deductible}
                  items={WME_Deductibles[specificCurrency[id]]}
                  disabled={loadingStatus[id]}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel sx={{ width: 260, mb: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }} control={<SelectPlanCheckbox size="small" readOnly={isComparing} sx={comparingCheckboxSx} onReadOnlyChange={onComparingLockedChange} onChangeCb={scrollToView} selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='bwho_ww' />} label="Worldwide Wellbeing" />
                <CustomSelect2
                  dense
                  sx={{ minWidth: 170 }}
                  readOnly
                  value={deductibles.bwho.ww_deductible}
                  items={WW_Deductibles[specificCurrency[id]]}
                  disabled={loadingStatus[id]}
                />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1.5 }}>
                <Typography fontSize={15} fontWeight={600}>Assistance Cover</Typography>
                <FormControlLabel sx={{ mb: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }} control={<Checkbox size="small" checked={assistanceCovers.evacuation} sx={comparingCheckboxSx}
                  onChange={(e) => {
                    if (isComparing) {
                      setShowComparingLockedSnackbar(true);
                      return;
                    }

                    setAssistanceCovers({ ...assistanceCovers, evacuation: e.target.checked });
                    scrollToView();
                  }} />}
                  label="Worldwide Evacuation" />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize={13} fontWeight={600} sx={{ width: 170 }}>Area Of Coverage</Typography>
                <Select
                  size='small'
                  value={specificAreaOfCoverage[id]}
                  onChange={(e) => {
                    specificAreaOfCoverage[id] = e.target.value;
                    setSpecificAreaOfCoverage({ ...specificAreaOfCoverage });

                    addToPendingTasks([id]);
                  }}
                  sx={{ fontSize: 12, height: 32, width: '170px', minWidth: 170 }}
                  fullWidth
                  disabled={loadingStatus[id]}
                >
                  {
                    specific_area_of_coverages && specific_area_of_coverages.map(area_of_coverage => (
                      <MenuItem key={area_of_coverage} value={area_of_coverage} sx={{ fontSize: 12 }}>{area_of_coverage}</MenuItem>
                    ))
                  }

                </Select>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize={13} fontWeight={600} sx={{ width: 170 }}>Currency</Typography>
                <Select
                  size='small'
                  value={specificCurrency[id]}
                  onChange={(e) => {
                    specificCurrency[id] = e.target.value;
                    setSpecificCurrency({ ...specificCurrency });

                    // clearResponse();

                    setDeductibles(prev => {
                      return {
                        ...prev,
                        [id]: {
                          wmi_deductible: getSafe(() => appConfig.content.deductibles.bwho.wmi[specificCurrency[id]][0]),
                          wme_deductible: getSafe(() => appConfig.content.deductibles.bwho.wme[specificCurrency[id]][0]),
                          wmp_deductible: getSafe(() => appConfig.content.deductibles.bwho.wmp[specificCurrency[id]][0]),
                          ww_deductible: getSafe(() => appConfig.content.deductibles.bwho.ww[specificCurrency[id]][0])
                        },
                      };
                    });


                    addToPendingTasks([id]);
                  }}
                  sx={{ fontSize: 12, height: 32, width: '170px', minWidth: 170 }}
                  fullWidth
                  disabled={loadingStatus[id]}
                >
                  {
                    currencies && currencies.map(currency => (
                      <MenuItem key={currency} value={currency} sx={{ fontSize: 12 }}>{currency}</MenuItem>
                    ))
                  }

                </Select>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize={13} fontWeight={600} sx={{ width: 170 }}>Payment Frequency</Typography>
                <Select
                  size='small'
                  value={specificPaymentFrequency[id]}
                  onChange={(e) => {
                    specificPaymentFrequency[id] = e.target.value;
                    setSpecificPaymentFrequency({ ...specificPaymentFrequency });

                    addToPendingTasks([id]);
                  }}
                  sx={{ fontSize: 12, height: 32, width: '170px', minWidth: 170 }}
                  fullWidth
                  disabled={loadingStatus[id]}
                >
                  {
                    ["Annually", "Semi Annually", "Quarterly", "Monthly"].map(payment_frequency => (
                      <MenuItem key={payment_frequency} value={payment_frequency} sx={{ fontSize: 12 }}>{payment_frequency}</MenuItem>
                    ))
                  }

                </Select>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize={13} fontWeight={600} sx={{ width: 170 }}>Renewal/Further Discount</Typography>
                <PercentInput
                  value={discounts[id]}
                  onCommit={(value) => setDiscounts(prev => ({ ...prev, [id]: value }))}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize={13} fontWeight={600} sx={{ width: 170 }}>Renewal/Further Loading</Typography>
                <PercentInput
                  value={loadings[id]}
                  onCommit={(value) => setLoadings(prev => ({ ...prev, [id]: value }))}
                />
              </Box>
            </Box>
          </Box>

          {
            isExpandedApiRequestPerInsurers[id] ? (
              <div>
                {/* Zero Deductible */}
                <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'end', width: '100%', marginBottom: 7 }}>
                  <TableViewModeToggleButton
                    id={id}
                    isExpandedApiRequestPerInsurers={isExpandedApiRequestPerInsurers}
                    setIsxpandedApiRequestPerInsurers={setIsxpandedApiRequestPerInsurers}
                    addToPendingTasks={() => {}} // don't add to pending tasks
                    style={{ marginRight: 7, fontSize: 11, padding: '2px 8px', minHeight: 0 }}
                    sx={{ '& .MuiSvgIcon-root': { fontSize: 14 } }}
                  />
                  <CopyExcelDataButton
                    elementId='table-bwho-rates-zero-deductible'
                    style={{ marginRight: 7, fontSize: 11, padding: '2px 8px', minHeight: 0 }}
                    sx={{ '& .MuiSvgIcon-root': { fontSize: 14 } }}
                  />
                  <CopyImageButton
                    elementId='table-bwho-rates-zero-deductible'
                    setShowImageCopy={setShowImageCopy}
                    style={{ fontSize: 11, padding: '2px 8px', minHeight: 0 }}
                    sx={{ '& .MuiSvgIcon-root': { fontSize: 14 } }}
                  />
                </div>
                <TableContainer component={Paper} sx={{ mb: 2, overflowX: 'auto' }}>
                  <Table ref={tableRef} id="table-bwho-rates-zero-deductible" size='small' sx={{ minWidth: 650, width: `calc(100% + ${clients.length > 2 ? clients.length - 3 : 0} * 75px)`, background: '#fff', }} >
                    <TableHead>
                      <TableRow style={{ background: '#0c1b4e', color: '#fff' }}>
                        <TableCell
                          style={{ width: 250, minWidth: 250, color: '#fff', fontWeight: 600, backgroundColor: '#d50000' }}
                          colSpan={2}
                        >Zero Deductible Plans</TableCell>
                        <TableCell
                          style={{ width: 100, minWidth: 100, color: '#fff', fontWeight: 600, backgroundColor: '#4caf50' }}
                        >Choice</TableCell>
                        {
                          clients.map((client, idx) => {
                            if (client.full_name.trim() === '') {
                              return <TableCell style={{ width: 200, fontWeight: 600, color: '#fff' }} key={idx}>_</TableCell>
                            }

                            return <TableCell style={{ width: 200, color: '#fff', fontWeight: 600 }} key={idx}><div>{client.full_name} ({client.age})</div><div>{client.country_of_residence}</div>  </TableCell>
                          })
                        }
                        <TableCell style={{ color: '#fff', fontWeight: 600 }}>Subtotal</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow style={{ height: maxRowHeight }}>
                        <TableCellText style={{ width: 250, minWidth: 250 }} content='Worldwide Medical Insurance' bgColor='#ffcdd2' />
                        <TableCellText style={{ width: 175, minWidth: 175 }} content='Zero Deductible' bgColor='#ffcdd2' />
                        <TableCellText style={{ minWidth: 100 }} content='Compulsory' bgColor='#c8e6c9' />
                        {
                          clients.map((client, idx) => {
                            return <TableCellRate
                              key={client.uid}
                              rate={getRateForExpandedTable(idx, 'Zero Deductible', 'wmi')}
                              currency={getCurrency()}
                              isLoading={loadingStatus[id]}
                              bgColor='#90caf9'
                            />;
                          })
                        }

                        <TableCellRate
                          rate={getRateSubTotalForExpandedTable('Zero Deductible', 'wmi')}
                          currency={getCurrency()}
                          isLoading={loadingStatus[id]}
                          bgColor='#90caf9'
                        />
                      </TableRow>
                      <TableRow sx={{ height: maxRowHeight}}>
                        <TableCellText style={{ width: 250, minWidth: 250 }} content='Worldwide Medical Plus' bgColor='#ffcdd2' />
                        <TableCellText style={{ width: 175, minWidth: 175 }} content='Zero Deductible' bgColor='#ffcdd2' />
                        <TableCellText style={{ minWidth: 100 }} content='Optional' bgColor='#c8e6c9' />
                        {
                          clients.map((client, idx) => {
                            return <TableCellRate
                              key={client.uid}
                              rate={getRateForExpandedTable(idx, 'Zero Deductible', 'wmp')}
                              currency={getCurrency()}
                              isLoading={loadingStatus[id]}
                              bgColor='#90caf9'
                            />;
                          })
                        }

                        <TableCellRate
                          rate={getRateSubTotalForExpandedTable('Zero Deductible', 'wmp')}
                          currency={getCurrency()}
                          isLoading={loadingStatus[id]}
                          bgColor='#90caf9'
                        />
                      </TableRow>
                      <TableRow sx={{ height: maxRowHeight}}>
                        <TableCellText style={{ width: 250, minWidth: 250 }} content='Worldwide Medicine & Equipment' bgColor='#ffcdd2' />
                        <TableCellText style={{ width: 175, minWidth: 175 }} content='Zero Deductible' bgColor='#ffcdd2' />
                        <TableCellText style={{ minWidth: 100 }} content='Optional' bgColor='#c8e6c9' />
                        {
                          clients.map((client, idx) => {
                            return <TableCellRate
                              key={client.uid}
                              rate={getRateForExpandedTable(idx, 'Zero Deductible', 'wme')}
                              currency={getCurrency()}
                              isLoading={loadingStatus[id]}
                              bgColor='#90caf9'
                            />;
                          })
                        }

                        <TableCellRate
                          rate={getRateSubTotalForExpandedTable('Zero Deductible', 'wme')}
                          currency={getCurrency()}
                          isLoading={loadingStatus[id]}
                          bgColor='#90caf9'
                        />
                      </TableRow>
                      <TableRow sx={{ height: maxRowHeight}}>
                        <TableCellText style={{ width: 250, minWidth: 250 }} content='Worldwide Wellbeing' bgColor='#ffcdd2' />
                        <TableCellText style={{ width: 175, minWidth: 175 }} content='Zero Deductible' bgColor='#ffcdd2' />
                        <TableCellText style={{ minWidth: 100 }} content='Optional' bgColor='#c8e6c9' />
                        {
                          clients.map((client, idx) => {
                            return <TableCellRate
                              key={client.uid}
                              rate={getRateForExpandedTable(idx, 'Zero Deductible', 'ww')}
                              currency={getCurrency()}
                              isLoading={loadingStatus[id]}
                              bgColor='#90caf9'
                            />;
                          })
                        }

                        <TableCellRate
                          rate={getRateSubTotalForExpandedTable('Zero Deductible', 'ww')}
                          currency={getCurrency()}
                          isLoading={loadingStatus[id]}
                          bgColor='#90caf9'
                        />
                      </TableRow>
                      <TableRow sx={{ height: maxRowHeight}}>
                        <TableCellText style={{ width: 250, minWidth: 250 }} content='Worldwide Evacuation' bgColor='#ffcdd2' />
                        <TableCellText style={{ width: 180, minWidth: 180 }} content='Zero Deductible' bgColor='#ffcdd2' />
                        <TableCellText style={{ minWidth: 100 }} content='Optional' bgColor='#c8e6c9' />
                        {
                          clients.map((client, idx) => {
                            return <TableCellRate
                              key={client.uid}
                              rate={getRate(idx, 'evacuation')}
                              currency={getCurrency()}
                              isLoading={loadingStatus[id]}
                              bgColor='#90caf9'
                            />;
                          })
                        }

                        <TableCellRateSubtotalPerPlan
                          computedRates={getComputedRatesMemo()}
                          planName='evacuation'
                          currency={getCurrency()}
                          isLoading={loadingStatus[id]}
                          specificCurrency={specificCurrency[id]}
                          bgColor='#90caf9'
                        />

                      </TableRow>
                    </TableBody>
                  </Table>

                </TableContainer>

                {/* With Deductible */}
                <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'end', width: '100%', marginBottom: 7 }}>
                  <CopyExcelDataButton
                    elementId='table-bwho-rates-with-deductible'
                    style={{ marginRight: 7, fontSize: 11, padding: '2px 8px', minHeight: 0 }}
                    sx={{ '& .MuiSvgIcon-root': { fontSize: 14 } }}
                  />
                  <CopyImageButton
                    elementId='table-bwho-rates-with-deductible'
                    setShowImageCopy={setShowImageCopy}
                    style={{ marginRight: 7, fontSize: 11, padding: '2px 8px', minHeight: 0 }}
                    sx={{ '& .MuiSvgIcon-root': { fontSize: 14 } }}
                  />
                </div>
                <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                  <Table id="table-bwho-rates-with-deductible" size='small' sx={{ minWidth: 650, background: '#fff', width: `calc(100% + ${clients.length > 2 ? clients.length - 3 : 0} * 75px)` }} >
                    <TableHead>
                      <TableRow style={{ background: '#0c1b4e', color: '#fff' }}>
                        <TableCell
                          style={{ width: 250, minWidth: 250, color: '#fff', fontWeight: 600, backgroundColor: '#d50000' }}
                          colSpan={2}
                        >Plans with Deductibles</TableCell>
                        <TableCell
                          style={{width: 100, minWidth: 100, color: '#fff', fontWeight: 600, backgroundColor: '#4caf50' }}
                        >Choice</TableCell>
                        {
                          clients.map((client, idx) => {
                            if (client.full_name.trim() === '') {
                              return <TableCell style={{ width: 200, fontWeight: 600, color: '#fff' }} key={idx}>_</TableCell>
                            }

                            return <TableCell style={{ width: 200, color: '#fff', fontWeight: 600 }} key={idx}><div>{client.full_name} ({client.age})</div><div>{client.country_of_residence}</div>  </TableCell>
                          })
                        }
                        <TableCell style={{ color: '#fff', fontWeight: 600 }}>Subtotal</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {
                        WMI_Deductibles[specificCurrency[id]].filter(deductible =>  {
                          return deductible !== 'Zero Deductible';
                        }).map((deductible, idx) => {
                          
                          return (
                            <TableRow style={{ height: maxRowHeight}}>

                              {
                                idx === 0 && <TableCellText rowSpan={WMI_Deductibles[specificCurrency[id]].length - 1} style={{ width: 250 }} content='Worldwide Medical Insurance' bgColor='#ffcdd2' />
                              }
                              <TableCellText style={{ width: 180 }} content={deductible} bgColor='#ffcdd2' />            
                              {
                                idx === 0 && <TableCellText rowSpan={WMI_Deductibles[specificCurrency[id]].length - 1} content={"Compulsory\n\nSelect one plan with your preferred Deductible"} bgColor='#c8e6c9' />
                              }

                              {
                                clients.map((client, idx) => {
                                  return <TableCellRate
                                    key={client.uid}
                                    rate={getRateForExpandedTable(idx, deductible, 'wmi')}
                                    currency={getCurrency()}
                                    isLoading={loadingStatus[id]}
                                    bgColor='#90caf9'                                
                                    />;
                                })
                              }

                              <TableCellRate
                                rate={getRateSubTotalForExpandedTable(deductible, 'wmi')}
                                currency={getCurrency()}
                                isLoading={loadingStatus[id]}
                                bgColor='#90caf9'
                              />
                            </TableRow>
                          );
                        })
                      }


                      <TableRow style={{ backgroundColor: '#0c1b4e' }}>
                        <TableCell colSpan={4 + clients.length} >&nbsp;</TableCell>
                      </TableRow>


                      <TableRow style={{ height: maxRowHeight}}>
                        <TableCellText style={{ width: 250, minWidth: 250 }} content='Worldwide Medical Plus' bgColor='#ffcdd2' />
                        <TableCellText style={{ width: 180, minWidth: 180 }} content={WMP_Deductibles[specificCurrency[id]][1]} bgColor='#ffcdd2' />
                        <TableCellText content='Optional' bgColor='#c8e6c9' />
                        {
                          clients.map((client, idx) => {
                            return <TableCellRate
                              key={client.uid}
                              rate={getRateForExpandedTable(idx, WMP_Deductibles[specificCurrency[id]][1], 'wmp')}
                              currency={getCurrency()}
                              isLoading={loadingStatus[id]}
                              bgColor='#90caf9'
                            />;
                          })
                        }

                        <TableCellRate
                          rate={getRateSubTotalForExpandedTable(WMP_Deductibles[specificCurrency[id]][1], 'wmp')}
                          currency={getCurrency()}
                          isLoading={loadingStatus[id]}
                          bgColor='#90caf9'
                        />
                      </TableRow>
                      <TableRow style={{ height: maxRowHeight}}>
                        <TableCellText style={{ width: 250, minWidth: 250 }} content='Worldwide Medicine & Equipment' bgColor='#ffcdd2' />
                        <TableCellText style={{ width: 180, minWidth: 180 }} content={WME_Deductibles[specificCurrency[id]][1]} bgColor='#ffcdd2' />
                        <TableCellText style={{ minWidth: 100 }} content='Optional' bgColor='#c8e6c9' />
                        {
                          clients.map((client, idx) => {
                            return <TableCellRate
                              key={client.uid}
                              rate={getRateForExpandedTable(idx, WME_Deductibles[specificCurrency[id]][1], 'wme')}
                              currency={getCurrency()}
                              isLoading={loadingStatus[id]}
                              bgColor='#90caf9'
                            />;
                          })
                        }

                        <TableCellRate
                          rate={getRateSubTotalForExpandedTable(WME_Deductibles[specificCurrency[id]][1], 'wme')}
                          currency={getCurrency()}
                          isLoading={loadingStatus[id]}
                          bgColor='#90caf9'
                        />
                      </TableRow>
                      <TableRow style={{ height: maxRowHeight}}>
                        <TableCellText style={{ width: 250, minWidth: 250 }} content='Worldwide Wellbeing' bgColor='#ffcdd2' />
                        <TableCellText style={{ width: 180, minWidth: 180 }} content='Zero Deductible' bgColor='#ffcdd2' />
                        <TableCellText style={{ minWidth: 100 }} content='Optional' bgColor='#c8e6c9' />
                        {
                          clients.map((client, idx) => {
                            return <TableCellRate
                              key={client.uid}
                              rate={getRateForExpandedTable(idx, 'Zero Deductible', 'ww')}
                              currency={getCurrency()}
                              isLoading={loadingStatus[id]}
                              bgColor='#90caf9'
                            />;
                          })
                        }

                        <TableCellRate
                          rate={getRateSubTotalForExpandedTable('Zero Deductible', 'ww')}
                          currency={getCurrency()}
                          isLoading={loadingStatus[id]}
                          bgColor='#90caf9'
                        />
                      </TableRow>
                      <TableRow style={{ height: maxRowHeight}}>
                        <TableCellText style={{ width: 250 }} content='Worldwide Evacuation' bgColor='#ffcdd2' />
                        <TableCellText style={{ width: 180 }} content='Zero Deductible' bgColor='#ffcdd2' />
                        <TableCellText content='Optional' bgColor='#c8e6c9' />
                        {
                          clients.map((client, idx) => {
                            return <TableCellRate
                              key={client.uid}
                              rate={getRate(idx, 'evacuation')}
                              currency={getCurrency()}
                              isLoading={loadingStatus[id]}
                              bgColor='#90caf9'
                            />;
                          })
                        }

                        <TableCellRateSubtotalPerPlan
                          computedRates={getComputedRatesMemo()}
                          planName='evacuation'
                          currency={getCurrency()}
                          isLoading={loadingStatus[id]}
                          specificCurrency={specificCurrency[id]}
                          bgColor='#90caf9'
                        />

                      </TableRow>
                    </TableBody>
                  </Table>

                </TableContainer>
              </div>
            ) : (
              <div>
                <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'end', width: '100%', marginBottom: 7 }}>
                  <TableViewModeToggleButton
                    id={id}
                    isExpandedApiRequestPerInsurers={isExpandedApiRequestPerInsurers}
                    setIsxpandedApiRequestPerInsurers={setIsxpandedApiRequestPerInsurers}
                    addToPendingTasks={() => {}} // don't add to pending tasks
                    style={{ marginRight: 7, fontSize: 11, padding: '2px 8px', minHeight: 0 }}
                    sx={{ '& .MuiSvgIcon-root': { fontSize: 14 } }}
                  />
                  <CopyExcelDataButton
                    elementId='table-bwho-rates'
                    style={{ marginRight: 7, fontSize: 11, padding: '2px 8px', minHeight: 0 }}
                    sx={{ '& .MuiSvgIcon-root': { fontSize: 14 } }}
                  />
                  <CopyImageButton
                    elementId='table-bwho-rates'
                    setShowImageCopy={setShowImageCopy}
                    style={{ fontSize: 11, padding: '2px 8px', minHeight: 0 }}
                    sx={{ '& .MuiSvgIcon-root': { fontSize: 14 } }}
                  />
                </div>

                <TableContainer component={Paper} sx={{ overflow: 'visible', mb: 2.5 }}>
                  <Table id="table-bwho-rates" size='small' style={{ minWidth: 650, background: '#fff', width: `calc(100% + ${clients.length > 2 ? clients.length - 3 : 0} * 75px)` }} >
                    <TableHead>
                      <TableRow style={{ background: '#0c1b4e', color: '#fff' }}>
                        <TableCell style={{ width: 250, color: '#fff', fontWeight: 600 }}>Plan</TableCell>
                        {
                          clients.map((client, idx) => {
                            if (client.full_name.trim() === '') {
                              return <TableCell style={{ width: 200, fontWeight: 600, color: '#fff' }} key={idx}>_</TableCell>
                            }

                            return <TableCell style={{ width: 200, color: '#fff', fontWeight: 600 }} key={idx}><div>{client.full_name} ({client.age})</div><div>{client.country_of_residence}</div>  </TableCell>
                          })
                        }
                        <TableCell style={{ color: '#fff', fontWeight: 600 }}>Subtotal</TableCell>
                      </TableRow>

                    </TableHead>
                    <TableBody>
                      <TableRatesRow
                        planId='bwho_wmi'
                        planName='wmi'
                        planLabel='Worldwide Medical Insurance'
                        clients={clients}
                        selectedPlans={selectedPlans}
                        loading={loadingStatus[id]}
                        rates={response ? response.rates : []}
                        currency={currency}
                        discount={discounts[id]}
                        additionalLoading={loadings[id]}
                        visible={selectedPlans.includes('bwho_wmi')}
                      />
                      <TableRatesRow
                        planId='bwho_wmp'
                        planName='wmp'
                        planLabel='Worldwide Medical Plus'
                        clients={clients}
                        selectedPlans={selectedPlans}
                        loading={loadingStatus[id]}
                        rates={response ? response.rates : []}
                        currency={currency}
                        discount={discounts[id]}
                        additionalLoading={loadings[id]}
                        visible={selectedPlans.includes('bwho_wmp')}
                      />
                      <TableRatesRow
                        planId='bwho_wme'
                        planName='wme'
                        planLabel='Worldwide Medicines & Equipment'
                        clients={clients}
                        selectedPlans={selectedPlans}
                        loading={loadingStatus[id]}
                        rates={response ? response.rates : []}
                        currency={currency}
                        discount={discounts[id]}
                        additionalLoading={loadings[id]}
                        visible={selectedPlans.includes('bwho_wme')}
                      />
                      <TableRatesRow
                        planId='bwho_ww'
                        planName='ww'
                        planLabel='Worldwide Wellbeing'
                        clients={clients}
                        selectedPlans={selectedPlans}
                        loading={loadingStatus[id]}
                        rates={response ? response.rates : []}
                        currency={currency}
                        discount={discounts[id]}
                        additionalLoading={loadings[id]}
                        visible={selectedPlans.includes('bwho_ww')}
                      />
                      {/* {
                        assistanceCovers.evacuation === true &&
                        <TableRow style={{ backgroundColor: '#ffffff' }}>
                          <TableCell colSpan={2 + clients.length} style={{ fontWeight: 600, backgroundColor: '#fff' }}>Optional</TableCell>
                        </TableRow>
                      } */}

                      {/* <TableRow>
                  <TableCell colSpan={2 + clients.length} sx={{ fontWeight: 600 }}>Optional</TableCell>
                </TableRow> */}

                      <TableRatesRow
                        planId='bwho_evacuation'
                        planName='evacuation'
                        planLabel='Worldwide Evacuation'
                        clients={clients}
                        selectedPlans={selectedPlans}
                        loading={loadingStatus[id]}
                        rates={response ? response.assistance_covers : []}
                        currency={currency}
                        visible={assistanceCovers.evacuation}
                      />
                      {/* {
                  checkIfThereIsSelected_BWHO_Plan() == false &&
                  <TableRow>
                    <TableCell>No Plan Selected</TableCell>
                  </TableRow>
                } */}

                      {/* {
                        ['wmi', 'wmp', 'wme', 'ww'].map((plan, idx) => {
                          return (
                            selectedPlans.includes('bwho_' + plan) &&
                            <TableRow key={plan} sx={{ backgroundColor: '#ffffff' }}>
                              <TableCell sx={{ border: '2px solid #fff', fontWeight: 600, background: '#bdbdbd' }}>{getPlanLabel()}</TableCell>
                              {
                                clients.map((client, idx) => {
                                  return <TableCell sx={{ border: '2px solid #fff', background: '#bdbdbd' }} key={idx}> {loadingStatus[id] ? <Skeleton variant="rounded" width={100} height={20} /> : formatRate(computeTotalPremiumPerClient(idx))}</TableCell>
                                })
                              }
                              <TableCell sx={{ border: '2px solid #fff', background: '#bdbdbd' }}>{loadingStatus[id] ? <Skeleton variant="rounded" width={100} height={20} /> : formatRate(computeTotalPremiumPerPlan())}</TableCell>
                            </TableRow>
                          );
                        })
                      } */}

                      {
                        comparisons.length === 0 &&
                        <TableRow style={{ backgroundColor: '#ffffff' }}>
                          <TableCell style={{ border: '2px solid #fff', fontWeight: 600, background: '#bdbdbd' }}>
                            {getPlanLabel()}
                          </TableCell>
                          {
                            clients.map((client, idx) => {
                              return <TableCell style={{ border: '2px solid #fff', background: '#bdbdbd' }} key={idx}> {loadingStatus[id] ? <Skeleton variant="rounded" width={100} height={20} /> : formatRate(computeTotalPremiumPerClient(idx))}</TableCell>
                            })
                          }
                          <TableCell style={{ border: '2px solid #fff', background: '#bdbdbd' }}>{loadingStatus[id] ? <Skeleton variant="rounded" width={100} height={20} /> : formatRate(computeTotalPremiumPerPlan())}</TableCell>
                        </TableRow>
                      }
                      {
                        getSortedComparisons().map((comparison) => {
                          return (
                            <TableRow
                              key={comparison.id}
                              style={{ backgroundColor: '#ffffff' }}
                            >
                              <TableCell style={{ border: '2px solid #fff', fontWeight: 600, background: '#bdbdbd' }}>
                                {buildPlanLabel(comparison)}
                              </TableCell>
                              {
                                clients.map((client, idx) => {
                                  return <TableCell style={{ border: '2px solid #fff', background: '#bdbdbd' }} key={idx}> {loadingStatus[id] ? <Skeleton variant="rounded" width={100} height={20} /> : formatRate(computeTotalPremiumForPlansPerClient(comparison, idx))}</TableCell>
                                })
                              }
                              <TableCell style={{ border: '2px solid #fff', background: '#bdbdbd' }}>{loadingStatus[id] ? <Skeleton variant="rounded" width={100} height={20} /> : formatRate(computeTotalPremiumForPlansPerPlan(comparison))}</TableCell>
                            </TableRow>
                          );
                        })
                      }
                    </TableBody>
                  </Table>

                </TableContainer>

                {
                  isComparing &&
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mt: -1.5, mb: 1, mt: 1 }}>
                    {
                      getSortedComparisons().map((comparison) => (
                        <Chip
                          key={comparison.id}
                          size="small"
                          variant="outlined"
                          color="primary"
                          label={buildPlanLabel(comparison)}
                          onDelete={() => removeComparison(comparison.id)}
                          disabled={loadingStatus[id]}
                          sx={{ fontSize: 12 }}
                        />
                      ))
                    }
                  </Box>
                }

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 1, mb: 2.5, mt: isComparing ? 0 : -1.5 }}>
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<AddCircleIcon />}
                    onClick={openComparisonDialog}
                    disabled={loadingStatus[id]}
                    sx={{ fontSize: 12, textTransform: 'none' }}
                  >
                    Compare
                  </Button>
                  {
                    isComparing &&
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<ClearAllIcon />}
                      onClick={() => setComparisons([])}
                      disabled={loadingStatus[id]}
                      sx={{ fontSize: 12, textTransform: 'none' }}
                    >
                      Clear Comparison
                    </Button>
                  }
                </Box>
              </div>
            )
          }



          <NotesDisplay appConfig={appConfig} insurerId={id} />

          <RatesVersionSelect
            insurer={id}
            label="Rates Version"
            insurerRates={insurerRates}
            selectedRatesVersion={selectedRatesVersion}
            setSelectedRatesVersion={setSelectedRatesVersion}
            disabled={loadingStatus[id]}
            handleFetchAppConfig={handleFetchAppConfig}
            onChange={() => setShowRefreshNow(true)}
          />

        </Box>

        <Dialog open={comparisonDialogOpen} onClose={() => setComparisonDialogOpen(false)} fullWidth maxWidth="xs">
          <DialogTitle>Add To Comparison</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography fontSize={15} fontWeight={600}>Plan Type</Typography>
              <FormControlLabel
                sx={{ mb: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                control={<Checkbox size="small" checked={comparisonSelection.wmi} onChange={() => {}} inputProps={{ readOnly: true }} sx={{ cursor: 'default' }} />}
                label="Worldwide Medical Insurance"
              />
              <FormControlLabel
                sx={{ mb: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                control={<Checkbox size="small" checked={comparisonSelection.wmp} onChange={() => toggleComparisonSelection('wmp')} />}
                label="Worldwide Medical Plus"
              />
              <FormControlLabel
                sx={{ mb: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                control={<Checkbox size="small" checked={comparisonSelection.wme} onChange={() => toggleComparisonSelection('wme')} />}
                label="Worldwide Medicines & Equipment"
              />
              <FormControlLabel
                sx={{ mb: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                control={<Checkbox size="small" checked={comparisonSelection.ww} onChange={() => toggleComparisonSelection('ww')} />}
                label="Worldwide Wellbeing"
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1.5 }}>
              <Typography fontSize={15} fontWeight={600}>Assistance Cover</Typography>
              <FormControlLabel
                sx={{ mb: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                control={<Checkbox size="small" checked={comparisonSelection.evacuation} onChange={() => toggleComparisonSelection('evacuation')} />}
                label="Worldwide Evacuation"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button size="small" onClick={() => setComparisonDialogOpen(false)}>Cancel</Button>
            <Button size="small" variant="contained" onClick={addComparison}>Add</Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={showComparingLockedSnackbar}
          autoHideDuration={3000}
          onClose={() => setShowComparingLockedSnackbar(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          message="Clear the comparison to view other modules"
        />

      </Paper>

    </Box>
  );
});

export default BWHO_Rates;